/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — screen 1, Template List
 * Purpose      : Templates as three tabs — Email Templates, SMS Templates, Template Review — over
 *                one shared "Message Templates" sidebar entry, rather than a separate "Template
 *                Review Queue" page. §9's original "grouped by channel and status" shape is
 *                historical: the per-status sections were flattened on 2026-08-06 (every row now
 *                carries its own status Chip) and status stopped driving row ORDER on 2026-08-27
 *                (see that Revision History entry). Channel still separates the tabs.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * §9 (tabs)      | 05-Aug-2026 | Claude         | Split Email/SMS into separate tabs (previously
 *                                                 one combined channel+status list) and folded
 *                                                 the standalone Template Review Queue page in
 *                                                 as a third tab, so the sidebar carries one
 *                                                 "Message Templates" entry instead of two.
 *                                                 `ReviewQueue`'s own routed page is unchanged —
 *                                                 its table rendering is now shared via the
 *                                                 exported `ReviewQueueTable`, not duplicated.
 * Comms draft/    | 05-Aug-2026 | Claude         | Switched Email/SMS/Review tabs to the new
 * publish                                         fetchManageCatalog() read (DRAFT/UNDER_REVIEW/
 *                                                 APPROVED/PUBLISHED) instead of the UNDER_REVIEW-
 *                                                 only review queue — closes the long-standing
 *                                                 "approved templates are not listed" gap. Added
 *                                                 a "Publish" row action for DRAFT/APPROVED rows.
 * Comms channel   | 06-Aug-2026 | Claude         | "New template" now seeds the editor's channel
 * from tab                                         from whichever tab (Email/SMS) it was clicked
 *                                                 on and locks it there — the tabs already split
 *                                                 by channel, so asking again on the next screen
 *                                                 was redundant. See TemplateEditor's own
 *                                                 `channelLocked` prop.
 * Comms row      | 06-Aug-2026 | Claude         | Each row grew its own status Chip inside the old
 * status                                          `ListItemButton`/`secondaryAction` per-status-
 *                                                 group layout, then went through four rounds of
 *                                                 flex-position corrections that same day (an
 *                                                 overlap from `secondaryAction`'s absolute
 *                                                 positioning, the chip sitting flush against the
 *                                                 buttons, its position drifting with button count,
 *                                                 and the now-redundant group-header chip) — moot
 *                                                 detail superseded by the table rewrite below;
 *                                                 see git history if the blow-by-blow ever matters.
 * Comms table     | 06-Aug-2026 | Claude         | Full redesign, at the user's request (reference:
 * redesign                                         tenant-web's already-built `PlansTable`/
 *                                                 `SmsTemplatesTable`, screenshotted as the target
 *                                                 look): replaced the per-status-group `Paper`/
 *                                                 `List` layout entirely with one flat MUI `Table`
 *                                                 per tab — Name (channel icon + name), Subject/
 *                                                 Message, Status (Chip), Action (icon buttons with
 *                                                 `Tooltip`s, same conditions as before). Row order
 *                                                 still comes from `groupByChannelAndStatus`'s
 *                                                 (statusRank, name) sort, just flattened rather
 *                                                 than rendered as separate sections. Deliberately
 *                                                 MUI throughout, not a port of tenant-web's
 *                                                 Tailwind/lucide-react classes — provider-web has
 *                                                 never used Tailwind, and introducing a second
 *                                                 styling system for one screen would be worse than
 *                                                 either system alone. All action `data-testid`s,
 *                                                 onClick handlers, and the row-status Chip's
 *                                                 `data-testid` are UNCHANGED from before this
 *                                                 rewrite — every pre-existing test that queries by
 *                                                 those keeps passing; only the tests asserting on
 *                                                 the now-removed `group-{channel}-{status}` Paper
 *                                                 wrapper and the group's own template count needed
 *                                                 rewriting, since that structure no longer exists.
 * Comms unify    | 06-Aug-2026 | Claude         | Merged the separate "New version" action into
 * edit action                                     "Edit", at the user's request — a PUBLISHED row
 *                                                 used to show a distinctly-labelled "New version"
 *                                                 icon instead of "Edit", exposing the version
 *                                                 increment as its own UI concept. There is now ONE
 *                                                 visible "Edit" action on every editable-in-some-
 *                                                 way status (DRAFT/APPROVED/REJECTED/
 *                                                 CHANGES_REQUESTED/PUBLISHED); its `onClick` still
 *                                                 picks between `openEditTemplate` (in place) and
 *                                                 `openNewVersion` (a revision) by status — same two
 *                                                 navigations as before, just behind one button.
 *                                                 `openNewVersion`/`new-version-button-*` are gone;
 *                                                 nothing calls `AddCircleOutline` any more either.
 * Comms drop     | 06-Aug-2026 | Claude         | Name column no longer appends "  ·  v{version}"
 * name version                                    after the template name, at the user's request —
 *                                                 the version number is still in every row's
 *                                                 `data-testid`/key/route (unchanged), just not
 *                                                 shown to the reader any more.
 * Comms remove   | 06-Aug-2026 | Claude         | Dropped the "Archive" row action entirely, at
 * archive action                                  the user's request — the button, its confirm
 *                                                 dialog, its success banner, and all of the
 *                                                 state/handlers behind them (`archiveTarget`,
 *                                                 `openArchiveDialog`/`confirmArchive`/etc.) are
 *                                                 gone. `retireTemplate` (`MessageTemplateService`)
 *                                                 and the backend `retire` endpoint it wraps are
 *                                                 UNTOUCHED — this removed the one place on this
 *                                                 screen that called it, not the capability itself.
 * Comms remove   | 06-Aug-2026 | Claude         | Dropped `QueueProvenanceGapBanner` from the
 * provenance                                       Template Review tab entirely, at the user's
 * banner                                           request — removed from its only other call
 *                                                 site (`ReviewQueue.tsx`'s own standalone page)
 *                                                 in the same change, since the component exists
 *                                                 specifically so both places show identical
 *                                                 wording; showing it in one place and not the
 *                                                 other would have been a strange inconsistency,
 *                                                 not a narrower fix. See `ReviewQueue.tsx`'s own
 *                                                 revision history and "RECORDED GAP" comment —
 *                                                 the underlying DTO gap the banner described is
 *                                                 unchanged, only its on-screen disclosure is gone.
 * Comms header   | 06-Aug-2026 | Claude         | Gave the table header row a subtle background
 * shading                                          (`grey.50`) instead of plain white, at the
 *                                                 user's request (reference: the same
 *                                                 tenant-web-styled screenshot used for the
 *                                                 original table redesign). Header row only —
 *                                                 the Template Review tab's own table
 *                                                 (`ReviewQueueTable`, a separate component) is
 *                                                 untouched, since only the Email/SMS table shown
 *                                                 in the screenshot was asked about.
 * Comms manage   | 06-Aug-2026 | Claude         | Backend fix, at the user's request, confirmed
 * catalog gap                                     directly against the database: a template a
 *                                                 reviewer sent back for rework (CHANGES_REQUESTED)
 *                                                 was invisible on this screen's Email/SMS tabs —
 *                                                 not UNDER_REVIEW any more so gone from the review
 *                                                 queue, and not one of the four statuses
 *                                                 `findManageCatalog` returned either. Same day, a
 *                                                 follow-up request: REJECTED rows too, missing for
 *                                                 the same reason. Fixed in
 *                                                 `MessageTemplateMasterRepository` (see its own
 *                                                 javadoc) — no change needed in THIS file, either
 *                                                 time, since `statusLabel`/`statusColor`/
 *                                                 `isDirectlyEditable` already handled both
 *                                                 statuses correctly; they just never arrived from
 *                                                 the backend to render.
 * SMS split PR-2 | 07-Aug-2026  | Claude         | Added a "Category" column, SMS tab only (the
 *                                                  vocabulary — LEAD/OPPORTUNITY/CUSTOMER/
 *                                                  WIN_BACK/SEASONAL — is SMS-only today, closing
 *                                                  the feature-parity gap the sms_template backfill
 *                                                  left: see MessageTemplateMaster#templateCategory
 *                                                  and V1_5_0_0_90). A row-level Select calls the
 *                                                  new setTemplateCategory() PATCH; omitting a
 *                                                  category never blocks Publish/Edit — it is purely
 *                                                  metadata, unlike every other column here.
 * template order | 27-Aug-2026  | Claude         | ROW ORDER NO LONGER COMES FROM
 *                                                  `groupByChannelAndStatus`. The 06-Aug-2026
 *                                                  "Comms table redesign" entry above is left as
 *                                                  written because it was true then; THIS entry
 *                                                  supersedes its ordering sentence. The render
 *                                                  call site now uses
 *                                                  `CommunicationTypes.sortForManageList` —
 *                                                  newest FAMILY first, a family's versions kept
 *                                                  together. Reason: `STATUS_ORDER` ranks DRAFT
 *                                                  5th of 6, so every newly created template was
 *                                                  pushed behind four other status blocks and
 *                                                  then placed alphabetically, routinely landing
 *                                                  on page 2-3 of a paginated table. Ranking by
 *                                                  status was also vestigial after that same
 *                                                  06-Aug redesign: every row now carries its own
 *                                                  status Chip, so status no longer needs to
 *                                                  drive position. Same row SET as before — only
 *                                                  the order changed.
 * template_kind  | 15-Sep-2026  | Claude         | Surfaces `templateKind` (SYSTEM / AUTOMATION /
 *                                                  MANUAL — HOW a template is sent, orthogonal to
 *                                                  templateCategory's WHAT): a "System"/"System ·
 *                                                  Default" chip in the Name cell, the Remove action
 *                                                  hidden on the SYSTEM DEFAULT row only (mirroring
 *                                                  archive()'s TEMPLATE_RETIRE_SYSTEM_DEFAULT guard,
 *                                                  which is SYSTEM *and* isDefault — a non-default
 *                                                  SYSTEM row is referenced by nothing and stays
 *                                                  removable), and a Kind filter beside the search.
 *                                                  Backend-complete already (TemplateSummaryDTO
 *                                                  carries the field); this is display + filtering
 *                                                  only, no new request and no new column.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useOidcAccessToken } from '@axa-fr/react-oidc';

import { AsyncBoundary, useAsyncResource } from './AsyncStates';
import {
  cloneTemplate,
  fetchManageCatalog,
  publishTemplate,
  retireTemplate,
} from './MessageTemplateService';
import {
  CHANNEL_EMAIL,
  CHANNEL_SMS,
  TemplateKind,
  TemplateSummary,
  sortForManageList,
  statusColor,
  statusLabel,
  templateKindLabel,
} from './CommunicationTypes';
import { bodyPreview, ReviewQueueTable } from './ReviewQueue';
import { TemplateVersionHistory } from './TemplateVersionHistory';
import { TemplateReviewThread } from './TemplateReviewThread';
import MessagePreview from './MessagePreview';

/** The three views this screen offers over the SAME underlying fetch — see the class-level
 *  doc comment below for why there is only ever one `useAsyncResource` behind all three. */
type TemplateListTab = 'email' | 'sms' | 'review';

/** The Kind filter's value: a real {@link TemplateKind}, or `ALL` for "do not filter". `ALL` is a
 *  UI-only sentinel deliberately kept OUT of `TemplateKind` — the column is NOT NULL and the
 *  vocabulary is total, so "any kind" is a question this screen asks, never a state a row is in. */
type TemplateKindFilter = TemplateKind | 'ALL';

const TEMPLATE_KIND_OPTIONS: TemplateKind[] = ['SYSTEM', 'AUTOMATION', 'MANUAL'];

/**
 * ============================== NO LONGER PARTIAL (2026-08-05) ==============================
 *
 * §9 wants every template grouped by channel and status. Until the draft/publish work this
 * screen could only show UNDER_REVIEW rows (the review queue was the only read provider-api
 * exposed) — the Email/SMS tabs carried a permanent "approved templates are not listed"
 * banner as a result.
 *
 * That gap is now closed by `fetchManageCatalog` (`GET /api/v1/message-templates/manage`),
 * which returns DRAFT, UNDER_REVIEW, APPROVED, CHANGES_REQUESTED, REJECTED (both added
 * 2026-08-06 — a tenant resubmission sent back for rework, or a rejected one, used to be
 * invisible here too, on top of the review queue; see
 * `MessageTemplateMasterRepository#findManageCatalog`'s own javadoc), and PUBLISHED rows —
 * a provider's own drafts, pending/sent-back-for-rework/rejected submissions, and
 * already-published templates are all visible here now, with a "Publish" action on anything
 * not yet live. This is a SEPARATE, provider-admin-only read from the tenant-facing live
 * catalog (`MessageTemplateMasterService.liveCatalog()`), which stays PUBLISHED-only and
 * untouched — see that method's own javadoc for why widening it would have been unsafe.
 *
 * The Template Review tab still wants UNDER_REVIEW rows ONLY (it is a reviewer's triage
 * queue, not a catalog) — it filters the one shared fetch down to that status client-side
 * rather than issuing a second request.
 *
 * ALSO NOT BUILT, DELIBERATELY: a `communication_template` screen. That table was never
 * created; `message_template_master` already is it. Building against the design's name
 * would have meant building against a table that does not exist.
 * ==============================================================================
 */

export const TemplateList: React.FC = () => {
  const { accessToken } = useOidcAccessToken();
  const navigate = useNavigate();

  /**
   * PR 8063 review (2026-08-13): the literal `calc(100vh - 94px)` below assumed a 64px MUI
   * Toolbar (its default only at the sm+ breakpoint — 56px at xs) plus MainLayout's fixed 30px
   * margin. Rather than hardcode either number, measure this component's own root Box's actual
   * distance from the top of the viewport and size off THAT — correct at every breakpoint, and
   * automatically correct if the Toolbar height or MainLayout's margin ever changes, with no
   * dependency on MainLayout.tsx internals. `calc(100vh - 94px)` is kept only as the one-frame
   * fallback before the first measurement runs, so there is no flash of an unbounded/zero-height
   * box on initial mount.
   */
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [availableHeight, setAvailableHeight] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      setAvailableHeight(window.innerHeight - el.getBoundingClientRect().top);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const { state, reload } = useAsyncResource<TemplateSummary[]>(
    () => fetchManageCatalog(accessToken),
    [accessToken],
  );

  // 2026-08-11: the 15s background auto-refresh was removed at the user's request — polling the list
  // on a timer is wasteful and can swap the rows out mid-action. Use the Refresh button instead.

  // Returning from ReviewDetail's "back" lands here with state.tab === 'review' so the reviewer
  // is dropped back on the Template Review tab (the standalone queue page was removed).
  const location = useLocation();
  const routedState = location.state as
    | { tab?: TemplateListTab; savedNotice?: { title: string; message: string } }
    | null;
  const initialTab = routedState?.tab ?? 'email';
  const [tab, setTab] = React.useState<TemplateListTab>(initialTab);
  // Client-side search over the already-loaded catalog (no backend search param). Matches template
  // name + subject; applied per-tab so each tab's empty state and rows reflect the query.
  const [search, setSearch] = React.useState('');
  // Kind filter (SYSTEM / AUTOMATION / MANUAL), client-side over the same loaded rows for the same
  // reason the search is: the catalog arrives whole, so a backend filter param would buy nothing
  // but a round trip. Applied on all three tabs — the Template Review queue can hold a kind the
  // reviewer wants to isolate just as the channel tabs can.
  const [kindFilter, setKindFilter] = React.useState<TemplateKindFilter>('ALL');

  // Client-side pagination of the rendered rows (same convention as the tenant Template Library /
  // Connection-plans tables — the catalog is already fully loaded, so this paginates in the browser,
  // no backend page param). Reset to page 0 whenever the tab or the search query changes, so you are
  // never stranded on a page that no longer exists for the new, shorter result set.
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  React.useEffect(() => {
    setPage(0);
  }, [tab, search, kindFilter]);

  // The TemplateEditor no longer stops on its own "Changes saved" screen — it navigates straight
  // here and hands the confirmation over as this banner (user request 2026-08-07). Seeded once
  // from router state so a refresh (which drops history state) clears it.
  const [savedNotice, setSavedNotice] = React.useState(routedState?.savedNotice ?? null);

  const [previewTarget, setPreviewTarget] = React.useState<TemplateSummary | null>(null);
  const [cloneTarget, setCloneTarget] = React.useState<TemplateSummary | null>(null);
  const [cloneName, setCloneName] = React.useState('');
  const [cloning, setCloning] = React.useState(false);
  const [cloneError, setCloneError] = React.useState<{
    message: string;
    httpStatus: number | null;
  } | null>(null);
  const [cloneSuccess, setCloneSuccess] = React.useState<TemplateSummary | null>(null);

  const cloneNameMissing = cloneName.trim() === '';

  const openCloneDialog = (template: TemplateSummary) => {
    setCloneError(null);
    setCloneName(`${template.name} Copy`);
    setCloneTarget(template);
  };

  const closeCloneDialog = () => {
    if (cloning) return;
    setCloneTarget(null);
    setCloneError(null);
  };

  const confirmClone = async () => {
    if (!cloneTarget || cloneNameMissing) return;
    setCloning(true);
    setCloneError(null);
    const result = await cloneTemplate(cloneTarget.templateMasterUid, cloneName.trim(), accessToken);
    setCloning(false);

    if (result.kind === 'ok') {
      setCloneSuccess(result.data);
      setCloneTarget(null);
      reload();
      return;
    }
    if (result.kind === 'forbidden') {
      setCloneError({
        message: 'Refused — cloning needs provider-admin permission.',
        httpStatus: 403,
      });
    } else if (result.kind === 'unauthorized') {
      setCloneError({ message: 'Your session is not valid. Sign in again.', httpStatus: 401 });
    } else if (result.kind === 'notFound') {
      // Includes "source template has no live (APPROVED) version" — shown verbatim, no
      // client-side rewording or code-based special-casing.
      setCloneError({ message: result.message, httpStatus: 404 });
    } else if (result.kind === 'rejected') {
      setCloneError({ message: result.message, httpStatus: result.status });
    } else {
      setCloneError({ message: result.message, httpStatus: result.status });
    }
  };

  const [publishTarget, setPublishTarget] = React.useState<TemplateSummary | null>(null);
  const [publishing, setPublishing] = React.useState(false);
  const [publishError, setPublishError] = React.useState<{
    message: string;
    httpStatus: number | null;
  } | null>(null);
  const [publishSuccess, setPublishSuccess] = React.useState<string | null>(null);

  const openPublishDialog = (template: TemplateSummary) => {
    setPublishError(null);
    setPublishTarget(template);
  };

  const closePublishDialog = () => {
    if (publishing) return;
    setPublishTarget(null);
    setPublishError(null);
  };

  // Remove (soft-delete) — the provider-side "delete" of a template. It calls the backend `retire`
  // endpoint, which sets is_deleted = true on every version of the family (a STATE change; the rows
  // are never physically deleted, so history stays intact). It 409s while any plan step still
  // references the template — that message is shown verbatim so the reviewer sees what blocks it.
  // The Archive row action was removed 2026-08-06 and re-wired here as "Remove" at the user's
  // request (2026-08-14); the retire endpoint/service it wraps were always kept for exactly this.
  const [removeTarget, setRemoveTarget] = React.useState<TemplateSummary | null>(null);
  const [removing, setRemoving] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<{
    message: string;
    httpStatus: number | null;
  } | null>(null);
  const [removeSuccess, setRemoveSuccess] = React.useState<string | null>(null);

  // Auto-dismiss the success banners (P5). provider-web has no useToast/ToastProvider, so these
  // four MUI success Alerts used to sit until the user clicked the X — unlike the tenant side
  // (useToast, 2600ms) and the rest of provider-web (Snackbar autoHideDuration≈4000ms). One shared
  // timer clears whichever are showing after 5s; the cleanup cancels it whenever a banner changes
  // or is closed first, so a stale timer can never wipe a freshly-shown banner. Error banners are
  // intentionally excluded — those should persist until the user reads/acts on them.
  React.useEffect(() => {
    if (!savedNotice && !cloneSuccess && !publishSuccess && !removeSuccess) return undefined;
    const timer = window.setTimeout(() => {
      setSavedNotice(null);
      setCloneSuccess(null);
      setPublishSuccess(null);
      setRemoveSuccess(null);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [savedNotice, cloneSuccess, publishSuccess, removeSuccess]);

  const openRemoveDialog = (template: TemplateSummary) => {
    setRemoveError(null);
    setRemoveTarget(template);
  };

  const closeRemoveDialog = () => {
    if (removing) return;
    setRemoveTarget(null);
    setRemoveError(null);
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    const result = await retireTemplate(removeTarget.templateMasterUid, accessToken);
    setRemoving(false);

    if (result.kind === 'ok') {
      setRemoveSuccess(`"${removeTarget.name}" was removed — it is no longer available to any tenant.`);
      setRemoveTarget(null);
      reload();
      return;
    }
    if (result.kind === 'forbidden') {
      setRemoveError({ message: 'Refused — removing a template needs provider-admin permission.', httpStatus: 403 });
    } else if (result.kind === 'unauthorized') {
      setRemoveError({ message: 'Your session is not valid. Sign in again.', httpStatus: 401 });
    } else if (result.kind === 'notFound') {
      setRemoveError({ message: result.message, httpStatus: 404 });
    } else {
      // Includes the 409 "still referenced by a plan step" case — the backend names what blocks it,
      // and that message is shown exactly as provided, with no client-side rewording.
      setRemoveError({ message: result.message, httpStatus: result.status });
    }
  };

  const confirmPublish = async () => {
    if (!publishTarget) return;
    setPublishing(true);
    setPublishError(null);
    const result = await publishTemplate(publishTarget.templateMasterUid, publishTarget.version, accessToken);
    setPublishing(false);

    if (result.kind === 'ok') {
      setPublishSuccess(`"${publishTarget.name}" was published — live for every tenant now.`);
      setPublishTarget(null);
      reload();
      return;
    }
    if (result.kind === 'forbidden') {
      setPublishError({
        message: 'Refused — publishing needs provider-admin permission.',
        httpStatus: 403,
      });
    } else if (result.kind === 'unauthorized') {
      setPublishError({ message: 'Your session is not valid. Sign in again.', httpStatus: 401 });
    } else if (result.kind === 'notFound') {
      setPublishError({ message: result.message, httpStatus: 404 });
    } else if (result.kind === 'rejected') {
      // Includes the 409 TEMPLATE_PUBLISH_NOT_ELIGIBLE case — the backend's message is shown
      // exactly as provided, with no client-side rewording or code-based special-casing.
      setPublishError({ message: result.message, httpStatus: result.status });
    } else {
      setPublishError({ message: result.message, httpStatus: result.status });
    }
  };

  /** The version-history modal's target. (The Usage action it was modelled on was removed on
   *  crm_phase2 at the user's request; this one stays — thread 66451 asked for it to be reachable.) */
  const [historyTarget, setHistoryTarget] = React.useState<TemplateSummary | null>(null);
  const [threadTarget, setThreadTarget] = React.useState<TemplateSummary | null>(null);

  const openReviewDetail = (template: TemplateSummary) => {
    navigate(`/communications/review/${template.templateMasterUid}/${template.version}`, {
      state: { row: template },
    });
  };

  /** Same eligible-status set MessageTemplateMasterService#editAsProvider enforces server-side —
   *  kept in sync deliberately, since a button visible on an ineligible row is just a 409
   *  waiting to happen, not a real option. */
  const isDirectlyEditable = (status: string) =>
    status === 'DRAFT' || status === 'APPROVED' || status === 'REJECTED' || status === 'CHANGES_REQUESTED';

  const openEditTemplate = (template: TemplateSummary) => {
    navigate('/communications/templates/new', { state: { template, editInPlace: true } });
  };

  /** A PUBLISHED row is live for every tenant, so it can never be edited in place
   *  (isDirectlyEditable above deliberately excludes it) — the only way to change it is a new
   *  version. This is the "New version" action's whole reason to exist: without it,
   *  TemplateEditor's already-implemented revision path (proposeChange -> approve -> publish,
   *  live immediately once saved) had no row action anywhere that reached it. */
  const openNewVersion = (template: TemplateSummary) => {
    navigate('/communications/templates/new', { state: { template } });
  };

  /**
   * A flat table of one channel's templates (2026-08-06, full redesign) — no more per-status
   * card sections. Order comes from `sortForManageList`: NEWEST FAMILY FIRST, versions of a
   * family kept together (see that helper for why status no longer ranks rows — every row already
   * carries its own status Chip, so the old (statusRank, name) order buried each new DRAFT behind
   * four other status blocks). Modeled on
   * this app's OWN existing table conventions elsewhere (tenant-web's `PlansTable`/
   * `SmsTemplatesTable`: a name-column icon, an icon-only Action column with tooltips, a
   * colour-pill Status column) — translated into this app's MUI idiom rather than that Tailwind
   * one, since provider-web has never used Tailwind and mixing the two within one screen is
   * worse than either alone.
   */
  const renderTable = (channelRows: TemplateSummary[]) => {
    // Clamp the page so a data reload/filter that shortens the list can never leave `page` pointing
    // past the end (which would render an empty table and make MUI warn about an out-of-range page).
    const pageCount = Math.max(1, Math.ceil(channelRows.length / rowsPerPage));
    const safePage = Math.min(page, pageCount - 1);
    const pagedRows = channelRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
    return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell>Name</TableCell>
            <TableCell>{tab === 'email' ? 'Subject' : 'Message'}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((template) => (
            <TableRow
              key={`${template.templateMasterUid}-${template.version}`}
              hover
              onClick={() => openReviewDetail(template)}
              data-testid={`template-row-${template.templateMasterUid}-${template.version}`}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  {tab === 'email'
                    ? <MailOutlineIcon fontSize="small" color="action" />
                    : <SmsOutlinedIcon fontSize="small" color="action" />}
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {template.name}
                  </Typography>
                  {/* Kind chip, SYSTEM only. It sits in the NAME cell, not beside the Status chip,
                      because kind and status are orthogonal axes — a chip in the Status column
                      would read as a second status ("System" instead of "Published") rather than
                      as what it is: who sends this template. `variant="outlined"` distinguishes it
                      from the filled status Chip at a glance without inventing a colour of its own;
                      a filled default-grey chip would be indistinguishable from the DRAFT/
                      SUPERSEDED status pill. AUTOMATION and MANUAL get no chip: AUTOMATION is the
                      default every pre-V1_5_3_6_8 row carries, so chipping it would badge almost
                      every row and say nothing.

                      "System · Default" vs plain "System" is NOT decoration — it is the whole
                      reason the two rows behave differently. Only the SYSTEM row that is ALSO the
                      channel+category default is resolved by application code, so only that one
                      refuses to be removed (see the Remove action below). Without this distinction
                      two rows that look identical would offer different actions, which is
                      unexplainable to the reader; with it, the chip states exactly why. */}
                  {template.templateKind === 'SYSTEM' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        template.isDefault === true
                          ? `${templateKindLabel(template.templateKind)} · Default`
                          : templateKindLabel(template.templateKind)
                      }
                      data-testid={`row-kind-${template.templateMasterUid}-${template.version}`}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell sx={{ maxWidth: 320 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {tab === 'email' ? (template.subject ?? '—') : bodyPreview(template.body)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={statusLabel(template.status)}
                  color={statusColor(template.status)}
                  data-testid={`row-status-${template.templateMasterUid}-${template.version}`}
                />
              </TableCell>
              {/* One stopPropagation on the wrapping Stack, not per button — bubbling reaches it
                  after any icon button's own onClick either way, so the row's own onClick (open
                  Review Detail) never fires for an action click. */}
              <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      aria-label="Preview"
                      data-testid={`preview-button-${template.templateMasterUid}-${template.version}`}
                      onClick={() => setPreviewTarget(template)}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/*
                    One "Edit" action for both cases, at the user's request (2026-08-06) — a
                    PUBLISHED row used to show a separately-labeled "New version" action instead
                    of Edit, exposing the version-increment as its own UI concept. To the author
                    there is no meaningful difference between "editing" a draft and "editing" a
                    published template; that the latter is implemented as a new row/version is a
                    backend detail (`createTemplateAsProvider`'s proposeChange path), not
                    something the UI needs a second button or label for. `openEditTemplate` (in
                    place) vs `openNewVersion` (a revision) is still exactly the same two
                    underlying navigations as before — only the VISIBLE button is now one thing.
                  */}
                  {/* 2026-08-11: Edit and Publish are per-row conditional, so a row missing one used
                      to shift every later icon left and the Action column read as ragged. Each is now
                      a fixed 30px slot — the icon when the action applies, an equal-width spacer when
                      it does not — so Preview / Edit / Clone / Publish line up as columns across rows.
                      Order (2026-08-11, at the user's request): Publish is LAST, with Clone before it. */}
                  {(isDirectlyEditable(template.status) || template.status === 'PUBLISHED') ? (
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label="Edit"
                        data-testid={`edit-button-${template.templateMasterUid}-${template.version}`}
                        onClick={() => (
                          template.status === 'PUBLISHED'
                            ? openNewVersion(template)
                            : openEditTemplate(template)
                        )}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box sx={{ width: 30, height: 30 }} />
                  )}
                  {/* Usage (BarChart) action removed 2026-08-11 at the user's request — the UsagePanel
                      component itself is left intact, only this row action is gone. */}
                  <Tooltip title="Clone">
                    <IconButton
                      size="small"
                      data-testid={`clone-button-${template.templateMasterUid}-${template.version}`}
                      onClick={() => openCloneDialog(template)}
                    >
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {(template.status === 'DRAFT' || template.status === 'APPROVED') ? (
                    <Tooltip title="Publish">
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Publish"
                        data-testid={`publish-button-${template.templateMasterUid}-${template.version}`}
                        onClick={() => openPublishDialog(template)}
                      >
                        <PublishOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box sx={{ width: 30, height: 30 }} />
                  )}
                  {/* Version history (thread 66451) — the template's lineage across every version of this
                      family, with a two-version diff. It was modelled on the Usage action, which
                      crm_phase2 removed on 2026-08-11; this one stays, because 66451 asked for the
                      already-built TemplateVersionHistory component to be reachable at all. */}
                  <Tooltip title="Version history">
                    <IconButton
                      size="small"
                      aria-label="Version history"
                      data-testid={`history-button-${template.templateMasterUid}-${template.version}`}
                      onClick={() => setHistoryTarget(template)}
                    >
                      <HistoryOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Review thread — provider parity with the tenant's per-family review-cycle
                      conversation (submission + GMS decision + note per version). Read-only; opens
                      TemplateReviewThread, which reuses the same /versions read the history dialog uses. */}
                  <Tooltip title="Review thread">
                    <IconButton
                      size="small"
                      aria-label="Review thread"
                      data-testid={`thread-button-${template.templateMasterUid}-${template.version}`}
                      onClick={() => setThreadTarget(template)}
                    >
                      <ForumOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* Remove (soft-delete): offered on the live PUBLISHED row — "removing" a template
                      means taking the live one away from every tenant. retire() soft-deletes the whole
                      family, so a DRAFT/APPROVED-only lineage is not separately removable here.

                      NOT offered on the SYSTEM DEFAULT row. This condition MIRRORS
                      `MessageTemplateMasterService.archive`'s TEMPLATE_RETIRE_SYSTEM_DEFAULT guard
                      exactly — SYSTEM *and* isDefault, not SYSTEM alone. That pair is precisely what
                      the resolver looks up, so the default is the only SYSTEM row any code path
                      references: retiring it leaves that business flow with no template and the
                      message silently stops going out. A NON-default SYSTEM row is referenced by
                      nothing, so removing it is legitimate and the UI must not forbid it — hiding
                      the action on the whole kind would block something the system genuinely
                      permits, an error that is invisible to the user in a way even a 409 is not.
                      The affordance differs between two SYSTEM rows, so the Name-cell chip carries
                      the distinction ("System · Default" vs "System") and the difference is
                      legible rather than mysterious. */}
                  {(template.status === 'PUBLISHED'
                    && !(template.templateKind === 'SYSTEM' && template.isDefault === true)) ? (
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Remove"
                        data-testid={`remove-button-${template.templateMasterUid}-${template.version}`}
                        onClick={() => openRemoveDialog(template)}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box sx={{ width: 30, height: 30 }} />
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {/* Client-side pagination (thread P3): the provider list was a plain unpaged table, so a tenant
          with many templates got one long scroll. Rows are already fully loaded, so we slice locally —
          same approach as the tenant TemplateLibrary. 10/25/50 per page; page resets on tab/search change. */}
      <TablePagination
        component="div"
        count={channelRows.length}
        page={safePage}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
    );
  };

  const tabChannel = tab === 'email' ? CHANNEL_EMAIL : CHANNEL_SMS;

  // Search over name + subject (case-insensitive) AND the Kind filter, in one pass. Applied to the
  // loaded rows before the per-tab channel/status split, so both compose with the existing tab
  // filtering. They live in ONE helper rather than two deliberately: every call site (each tab's
  // rows and each tab's `isEmpty` predicate) must apply exactly the same set, and a second helper
  // is a second thing to forget at one of those four places — which would show an empty table over
  // a non-empty `isEmpty`, or vice versa.
  const applyFilters = React.useCallback(
    <T extends { name?: string | null; subject?: string | null; templateKind?: string | null }>(
      list: T[],
    ): T[] => {
      const q = search.trim().toLowerCase();
      return list.filter((r) => {
        // A row whose kind the wire did not send is EXCLUDED by a specific kind filter rather than
        // assumed to be AUTOMATION (the column's default): "probably automation" is a guess, and a
        // filter that quietly includes guesses is worse than one that shows fewer rows.
        if (kindFilter !== 'ALL' && r.templateKind !== kindFilter) return false;
        if (!q) return true;
        return (r.name ?? '').toLowerCase().includes(q)
          || (r.subject ?? '').toLowerCase().includes(q);
      });
    },
    [search, kindFilter],
  );

  /** True when the reader has narrowed the list themselves — drives "nothing MATCHED" empty-state
   *  wording instead of "nothing EXISTS yet", which would be a lie with a filter applied. */
  const isFiltered = search.trim() !== '' || kindFilter !== 'ALL';

  return (
    // 2026-08-12: title/buttons/tabs kept fixed while only the table scrolls, at the user's
    // request. Two earlier attempts on this same fix both failed for the same underlying reason,
    // confirmed by reading MainLayout.tsx directly (this app has no browser/DevTools access, so
    // guessing was the only option until this read):
    //   1st (position: sticky, top: theme.mixins.toolbar.minHeight) — didn't account for the
    //      15px margin MainLayout.tsx's content Box also adds above the Toolbar spacer, so the
    //      sticky header stuck 15px too high and visually overlapped the AppBar.
    //   2nd (height: '100%' + flex column) — MainLayout.tsx's <main> has NO `display: flex`, so
    //      the Box inside it (`sx={{ flexGrow: 1, margin: '15px' }}`) has flexGrow with nothing to
    //      grow inside — it is really just an auto-height block. `height: '100%'` against an
    //      auto-height ancestor resolves to nothing (CSS spec: a percentage height is ignored when
    //      the parent's own height isn't explicitly set), so this Box never actually got bounded
    //      and just grew with its content, same as before.
    // This 3rd attempt sizes the box directly off the viewport instead of depending on that
    // ancestor chain at all: 64px is MUI's default Toolbar height (no custom `mixins.toolbar`
    // override exists in this app's theme — checked), and 30px is MainLayout's own 15px
    // margin-top + margin-bottom around that Toolbar spacer. `gmscloud-tenant-web`'s sibling
    // MainLayout.tsx already uses this exact `height: '100vh', overflow: 'hidden'` pattern for
    // the identical problem — proven prior art in this codebase, not a new technique.
    //
    // 2026-08-13 correction (PR 8063 review): 64px is only MUI's default Toolbar height at the
    // sm+ breakpoint — it's 56px at xs (viewport < 600px), so this literal reserved 8px too much
    // on a narrow viewport, and would silently drift if the Toolbar height ever changed (theme
    // customization). `availableHeight` (measured via `rootRef`, see above) replaces the literal
    // at runtime; `calc(100vh - 94px)` survives only as the one-frame fallback before the first
    // `useLayoutEffect` measurement lands.
    <Box
      ref={rootRef}
      sx={{
        p: 3,
        // maxHeight (not height): a short list (e.g. the default 10 rows) shrinks the frame to its
        // own content instead of stretching to the full viewport and leaving a large dead gap
        // below the table/pagination — the reported "big empty space". A long list still caps at
        // the measured available height and scrolls inside the flex child below, preserving the
        // fixed-frame behaviour PR 8063 introduced. `availableHeight` is measured from the toolbar/
        // margin at runtime; `calc(100vh - 94px)` remains the one-frame fallback before first measure.
        maxHeight: availableHeight != null ? `${availableHeight}px` : 'calc(100vh - 94px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
      data-testid="template-list"
    >
      <Box data-testid="template-list-header" sx={{ flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          {/* 2026-08-06: `variant="h5"` kept for the semantic heading element (a11y) — only its
              default size/weight is overridden here, to match the reference screenshots'
              compact, bold title instead of MUI's default h5, which reads oversized and thin
              next to a dense table. */}
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            Message templates
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={reload}>
            Refresh
          </Button>
          {tab !== 'review' && (
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/communications/templates/new', {
                // 2026-08-06: this button is only ever reached from the Email or SMS tab (it is
                // hidden on Review above), so the channel is already decided — seed it and lock
                // the editor's picker rather than asking the author to choose it again.
                state: {
                  template: { channel: tab === 'email' ? CHANNEL_EMAIL : CHANNEL_SMS },
                  channelLocked: true,
                },
              })}
            >
              New template
            </Button>
          )}
        </Stack>
      </Stack>

      {savedNotice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          data-testid="saved-notice-banner"
          onClose={() => setSavedNotice(null)}
        >
          <AlertTitle>{savedNotice.title}</AlertTitle>
          {savedNotice.message}
        </Alert>
      )}

      {cloneSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          data-testid="clone-success-banner"
          onClose={() => setCloneSuccess(null)}
        >
          <AlertTitle>Cloned</AlertTitle>
          &quot;{cloneSuccess.name}&quot; was created as a new draft — not yet live for any tenant.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              data-testid="clone-open-editor-button"
              onClick={() =>
                // A clone is a fresh DRAFT (never published), so it must open in EDIT-IN-PLACE mode
                // and save via editTemplate. Without `editInPlace`, the editor entered REVISION mode
                // and tried to proposeChange against a published version the clone has none of —
                // a 404 "no published (live) version to propose a change against" (2026-08-11 fix).
                navigate('/communications/templates/new', {
                  state: { template: cloneSuccess, editInPlace: true },
                })
              }
            >
              Open in editor
            </Button>
          </Box>
        </Alert>
      )}

      {publishSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          data-testid="publish-success-banner"
          onClose={() => setPublishSuccess(null)}
        >
          <AlertTitle>Published</AlertTitle>
          {publishSuccess}
        </Alert>
      )}

      {removeSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          data-testid="remove-success-banner"
          onClose={() => setRemoveSuccess(null)}
        >
          <AlertTitle>Removed</AlertTitle>
          {removeSuccess}
        </Alert>
      )}

      {/* Tabs (left) and the search box (right) share one row — the search sits inline at the end
          of the tab strip rather than a full-width box on its own line (2026-08-13, user request).
          The divider spans the whole row so the search still reads as part of the tab header. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tabs
          value={tab}
          onChange={(_event, next: TemplateListTab) => setTab(next)}
          aria-label="Message template views"
        >
          <Tab label="Email Templates" value="email" data-testid="tab-email-templates" />
          <Tab label="SMS Templates" value="sms" data-testid="tab-sms-templates" />
          <Tab label="Template Review" value="review" data-testid="tab-template-review" />
        </Tabs>
        {/* Kind filter + search share the end of the tab strip. The Select uses the same
            FormControl/InputLabel/Select idiom as the editor's own Category picker
            (TemplateEditor.tsx) — this app's one established dropdown shape — rather than a
            ToggleButtonGroup, which would put four competing buttons beside the three real tabs
            and read as a second tab strip.

            The search field is AB#30924's styled version (icon adornment, clear button, focus
            ring) kept intact; only its own ml/mb/flexShrink move up to this Stack, which now owns
            the spacing for both controls. */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, mb: 1, flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="template-kind-filter-label">Kind</InputLabel>
            <Select
              labelId="template-kind-filter-label"
              label="Kind"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as TemplateKindFilter)}
              inputProps={{ 'data-testid': 'template-kind-filter' }}
            >
              <MenuItem value="ALL">All kinds</MenuItem>
              {TEMPLATE_KIND_OPTIONS.map((kind) => (
                <MenuItem key={kind} value={kind}>{templateKindLabel(kind)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by template name or subject…"
            aria-label="Search templates"
            inputProps={{ 'data-testid': 'template-search' }}
            sx={{
              width: 340,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
                transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.light' },
                '&.Mui-focused': { boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}1f` },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>
      </Box>

      {/* The one flex child that actually grows/scrolls — `minHeight: 0` is required here, not
          optional: a flex item's default `min-height: auto` refuses to shrink below its content's
          intrinsic size, which would make this grow to fit every row instead of scrolling and
          push the whole page back into the original bug. */}
      <Box data-testid="template-list-scroll-region" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <AsyncBoundary
          state={state}
          what={tab === 'review' ? 'The template review queue' : 'The message template catalog'}
          onRetry={reload}
          loadingLabel={tab === 'review' ? 'Loading pending change requests…' : 'Loading templates…'}
          isEmpty={(rows) =>
            tab === 'review'
              ? applyFilters(rows.filter((row) => row.status === 'UNDER_REVIEW')).length === 0
              : applyFilters(rows.filter((row) => row.channel === tabChannel)).length === 0
          }
          emptyTitle={
            isFiltered
              ? 'No templates match your filters'
              : tab === 'review'
                ? 'No pending change requests'
                : `No ${tab === 'email' ? 'Email' : 'SMS'} templates yet`
          }
          emptyDetail={
            isFiltered
              ? 'No template on this tab matches the search or kind filter — clear them to see all.'
              : tab === 'review'
                ? 'Every tenant submission has been reviewed. This is a confirmed empty queue, not a failed load.'
                : 'Nothing of this channel exists yet — drafts, pending submissions, and published templates all show here.'
          }
        >
          {(rows) =>
            tab === 'review' ? (
              <ReviewQueueTable
                rows={applyFilters(rows.filter((row) => row.status === 'UNDER_REVIEW'))}
                onOpenDetail={openReviewDetail}
              />
            ) : (
              renderTable(
                applyFilters(
                  sortForManageList(rows.filter((r) => r.channel === tabChannel)),
                ),
              )
            )
          }
        </AsyncBoundary>
      </Box>

      <Dialog open={cloneTarget !== null} onClose={closeCloneDialog} data-testid="clone-confirm-dialog">
        <DialogTitle>Clone template</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new, independent template from &quot;{cloneTarget?.name}&quot;. Everything
            else (channel, subject, body) is copied from the current live version — give the copy
            a name.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Template name"
            value={cloneName}
            onChange={(event) => setCloneName(event.target.value)}
            error={cloneNameMissing}
            helperText={cloneNameMissing ? 'Required.' : `${cloneName.length}/200`}
            inputProps={{ maxLength: 200, 'data-testid': 'clone-name-input' }}
          />
          {cloneError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="clone-error">
              {cloneError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCloneDialog} disabled={cloning} data-testid="clone-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={confirmClone}
            disabled={cloning || cloneNameMissing}
            variant="contained"
            data-testid="clone-confirm-button"
          >
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={publishTarget !== null}
        onClose={closePublishDialog}
        data-testid="publish-confirm-dialog"
      >
        <DialogTitle>Publish template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Publish &quot;{publishTarget?.name}&quot;? This makes it live for every tenant —
            {publishTarget?.status === 'DRAFT'
              ? ' there is no separate human review step; publishing is the only approval this needs.'
              : ' it has already passed review and is only waiting to go live.'}
          </DialogContentText>
          {publishError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="publish-error">
              {publishError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePublishDialog} disabled={publishing} data-testid="publish-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={confirmPublish}
            disabled={publishing}
            variant="contained"
            data-testid="publish-confirm-button"
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onClose={closeRemoveDialog}
        data-testid="remove-confirm-dialog"
      >
        <DialogTitle>Remove template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove &quot;{removeTarget?.name}&quot;? This takes it away from every tenant. It is a
            soft delete — the template is marked removed, not erased, so its history is kept and it
            can be restored by support. A template still used by a plan step cannot be removed.
          </DialogContentText>
          {removeError && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="remove-error">
              {removeError.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog} disabled={removing} data-testid="remove-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={confirmRemove}
            disabled={removing}
            variant="contained"
            color="error"
            data-testid="remove-confirm-button"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>


      {historyTarget && (
        <TemplateVersionHistory template={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
      {threadTarget && (
        <TemplateReviewThread template={threadTarget} onClose={() => setThreadTarget(null)} />
      )}

      {previewTarget && (
        <MessagePreview
          templateMasterUid={previewTarget.templateMasterUid}
          version={previewTarget.version}
          name={previewTarget.name}
          channel={previewTarget.channel}
          onClose={() => setPreviewTarget(null)}
        />
      )}
    </Box>
  );
};

export default TemplateList;
